import {
  Avatar,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoCourseYear } from "@/shared/model/edo-course-year.model";
import { IEdoVideostorePost } from "@/shared/model/edo-videostore-post.model";
import { EdoEducationalStage } from "@/shared/model/enumerations/edo-educational-stage.model";
import { IEdoVideoStatusEnum } from "@/shared/model/enumerations/edo-video-status.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getEntities as getCourseYears } from "@/shared/reducers/entities/edo-course-year.reducer";
import { getSubjectsWithVideostorePosts, SubjectState } from "@/shared/reducers/entities/edo-subject.reducer";
import { getEntities as getVideostorePosts } from "@/shared/reducers/entities/edo-videostore-post.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { DeleteDialogType, EditDialogType, ViewDialogType } from "@/shared/util/dialog-utils";
import { ITEMS_PER_PAGE, ITEMS_PER_PAGE_OPTIONS } from "@/shared/util/pagination.constants";
import { AlertCircle, FileText, Loader2, Pencil, Plus, Trash2, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IPaginationBaseState, translate, Translate } from "react-jhipster";
import z from "zod";
import DeleteVideostorePost from "./components/DeleteVideostorePost";
import EditVideostorePost from "./components/EditVideostorePost";
import MultiUpload from "./components/MultiUpload";
import VideostoreDrafts from "./components/VideostoreDrafts";
import ViewVideoDialog from "./components/ViewVideoDialog";

export const getVideoStatusLabel = (status?: IEdoVideoStatusEnum): React.ReactNode => {
  if (status === IEdoVideoStatusEnum.UPLOADING) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-mockup-text-mid)]">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        Caricamento in corso...
      </div>
    );
  }
  if (status === IEdoVideoStatusEnum.UPLOADED) {
    return "Il video è stato caricato correttamente. È in attesa di elaborazione.";
  }
  if (status === IEdoVideoStatusEnum.PROCESSING) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-mockup-text-mid)]">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        Stiamo processando il tuo video...
      </div>
    );
  }
  if (status === IEdoVideoStatusEnum.ERROR) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-mockup-coral)]">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Si è verificato un errore.
      </div>
    );
  }
  if (status === IEdoVideoStatusEnum.READY) {
    return "Il tuo video è pronto.";
  }
  return "Stato sconosciuto";
};

const educationalStages = [EdoEducationalStage.SECONDARIA_II, EdoEducationalStage.SECONDARIA_I];

const subjectFilterSchema = z.object({ subjectFilter: z.string() });
type SubjectFilterFormValues = z.infer<typeof subjectFilterSchema>;

const VideostoreFilterContent = ({
  setSubjectFilter,
  edoSubjectList,
}: {
  setSubjectFilter: (v: number | null) => void;
  edoSubjectList: SubjectState["subjectsByVideostorePosts"];
}) => {
  const { control } = useFormContext<SubjectFilterFormValues>();
  return (
    <Controller
      name="subjectFilter"
      control={control}
      render={({ field }) => (
        <Select
          value={field.value}
          onValueChange={(v) => {
            field.onChange(v);
            setSubjectFilter(v === "NESSUNO" ? null : parseInt(v, 10));
          }}
        >
          <SelectTrigger className="min-w-[200px]">
            <SelectValue placeholder="Filtro materia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NESSUNO" disabled>
              Nessuno
            </SelectItem>
            {edoSubjectList.entities.map((subject) => (
              <SelectItem key={subject.id} value={String(subject.id)}>
                {subject.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
};

export const Videostore = () => {
  const dispatch = useAppDispatch();
  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const edoVideostorePostList = useAppSelector<EntityState<IEdoVideostorePost>>(
    (state) => state.edoVideostorePost
  );
  const edoCourseYearList = useAppSelector<EntityState<IEdoCourseYear>>(
    (state) => state.edoCourseYear
  );
  const { subjectsByVideostorePosts: edoSubjectList } = useAppSelector<SubjectState>(
    (state) => state.edoSubject
  );
  const [stageTab, setStageTab] = useState<EdoEducationalStage>(EdoEducationalStage.SECONDARIA_II);
  const [subjectFilter, setSubjectFilter] = useState<number | null>(null);
  const [draftsModalOpen, setDraftsModalOpen] = useState(false);
  const [editVideostorePostDialog, setEditVideostorePostDialog] =
    useState<EditDialogType<IEdoVideostorePost>>({ open: false, entity: null, isNew: false });
  const [deleteVideostorePostDialog, setDeleteVideostorePostDialog] =
    useState<DeleteDialogType<IEdoVideostorePost>>({ open: false, entity: null });
  const [viewVideoDialog, setViewVideoDialog] =
    useState<ViewDialogType<IEdoVideostorePost>>({ open: false, entity: null });
  const [multiUploadDialog, setMultiUploadDialog] = useState<{ open: boolean }>({ open: false });
  const [yearTab, setYearTab] = useState<number | null>(null);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "title",
    order: "ASC",
  });

  const handleChangeYearTab = (value: string) => setYearTab(parseInt(value, 10));

  const handlePagination = (page: number) =>
    setPaginationState({ ...paginationState, activePage: page + 1 });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const getVideostorePostList = () => {
    if (subjectFilter === null || yearTab === null) return;
    const params = new URLSearchParams();
    params.append("filter.educationalStage", "$eq:" + stageTab);
    params.append("filter.years.id", "$eq:" + yearTab);
    params.append("filter.draft", "$eq:0");
    params.append("filter.subject.id", "$eq:" + String(subjectFilter));
    dispatch(
      getVideostorePosts({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    dispatch(getCourseYears({ page: 0, size: 1000, sort: "year:ASC" }));
    dispatch(getSubjectsWithVideostorePosts());
  }, []);

  useEffect(() => {
    getVideostorePostList();
  }, [
    stageTab,
    yearTab,
    subjectFilter,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (edoSubjectList.entities.length) {
      setSubjectFilter(edoSubjectList.entities[0].id ?? null);
    }
  }, [edoSubjectList.entities]);

  useEffect(() => {
    if (edoCourseYearList.entities.length) {
      setYearTab(edoCourseYearList.entities[0].id ?? null);
    }
  }, [edoCourseYearList.entities]);

  useEffect(() => {
    if (edoVideostorePostList.updateSuccess) {
      getVideostorePostList();
    }
  }, [edoVideostorePostList.updateSuccess]);

  const isAdmin = hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_ADMIN]);

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        {/* Header toolbar */}
        <div className="flex flex-col gap-3 border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/30 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Form
              schema={subjectFilterSchema}
              defaultValues={{
                subjectFilter: subjectFilter != null ? String(subjectFilter) : "NESSUNO",
              }}
              onSubmit={() => {}}
              className="w-full min-w-0 sm:w-auto"
            >
              <VideostoreFilterContent
                setSubjectFilter={setSubjectFilter}
                edoSubjectList={edoSubjectList}
              />
            </Form>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setDraftsModalOpen(true)}
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--color-mockup-border)] bg-surface px-4 py-2 text-xs font-bold text-[var(--color-mockup-text-mid)] transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
              >
                <FileText className="h-4 w-4" />
                Bozze
              </button>
            )}
          </div>
          {isAdmin && (
            <div className="flex shrink-0 justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
                  disabled={edoVideostorePostList.loading}
                >
                  <Plus className="h-4 w-4" />
                  Aggiungi
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() =>
                    setEditVideostorePostDialog({ open: true, entity: null, isNew: true })
                  }
                >
                  <Plus className="h-4 w-4" />
                  Caricamento singolo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMultiUploadDialog({ open: true })}>
                  <Video className="h-4 w-4" />
                  Caricamento multiplo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )}
        </div>

        {/* Educational stage pills */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-mockup-border)] px-4 py-3">
          {educationalStages.map((stage) => (
            <button
              key={stage}
              type="button"
              onClick={() => setStageTab(stage)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                stageTab === stage
                  ? "bg-primary text-white shadow-md"
                  : "border border-[var(--color-mockup-border)] bg-surface text-[var(--color-mockup-text-mid)] hover:border-primary/30 hover:bg-primary/5"
              }`}
            >
              {translate(`edocendoApp.EdoEducationalStage.${stage}`)}
            </button>
          ))}
        </div>

        {/* Year tabs */}
        <Tabs
          value={yearTab != null ? String(yearTab) : ""}
          onValueChange={handleChangeYearTab}
          className="w-full"
        >
          <div className="border-b border-[var(--color-mockup-border)] px-4 py-3">
            <TabsList className="h-auto gap-2 bg-transparent p-0">
              {edoCourseYearList.entities.map((courseYear) => (
                <TabsTrigger
                  key={courseYear.id}
                  value={String(courseYear.id)}
                  disabled={
                    [4, 5].includes(courseYear.year ?? 0) &&
                    stageTab === EdoEducationalStage.SECONDARIA_I
                  }
                  className="rounded-full border border-[var(--color-mockup-border)] bg-surface px-4 py-1.5 text-xs font-bold shadow-sm data-[state=active]:bg-[var(--color-mockup-sky)] data-[state=active]:text-white data-[state=active]:border-transparent"
                >
                  {courseYear.year} {translate("edocendoApp.edoVideostorePost.year")}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        {/* Content */}
        {edoCourseYearList.loading && edoCourseYearList.entities.length === 0 ? (
          <div className="flex justify-center py-12">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            {edoVideostorePostList.loading && (
              <div className="flex justify-center py-8">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
            {edoVideostorePostList.entities?.length ? (
              <List className="divide-y divide-[var(--color-mockup-border)]">
                {edoVideostorePostList.entities.map((postVideostore: IEdoVideostorePost) => (
                  <ListItem
                    key={postVideostore.id}
                    className="flex w-full items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5"
                  >
                    <button
                      type="button"
                      onClick={() => setViewVideoDialog({ open: true, entity: postVideostore })}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <ListItemIcon className="!mr-0 shrink-0">
                        <Avatar className="h-10 w-10 rounded-2xl bg-primary/10 text-primary">
                          <Video className="h-5 w-5" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
                            {postVideostore.title}
                          </span>
                        }
                        secondary={
                          <span className="text-xs text-[var(--color-mockup-text-light)]">
                            {postVideostore.subject?.name}
                          </span>
                        }
                        className="min-w-0 flex-1"
                      />
                    </button>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-0.5">
                        <IconButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-[var(--color-mockup-text-light)] hover:bg-primary/10 hover:text-primary"
                          onClick={() =>
                            setEditVideostorePostDialog({
                              open: true,
                              entity: postVideostore,
                              isNew: false,
                            })
                          }
                          aria-label="Modifica"
                        >
                          <Pencil className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-[var(--color-mockup-text-light)] hover:bg-[var(--color-mockup-coral)]/10 hover:text-[var(--color-mockup-coral)]"
                          onClick={() =>
                            setDeleteVideostorePostDialog({ open: true, entity: postVideostore })
                          }
                          aria-label="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </IconButton>
                      </div>
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              !edoVideostorePostList.loading && (
                <div className="px-6 py-12 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Video className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                    <Translate contentKey="edocendoApp.edoVideostorePost.home.notFound" />
                  </p>
                </div>
              )
            )}
            <Pagination
              count={edoVideostorePostList.totalItems}
              page={paginationState.activePage - 1}
              rowsPerPage={paginationState.itemsPerPage}
              onPageChange={handlePagination}
              onRowsPerPageChange={handleRowsPerPage}
              rowsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Righe per pagina:"
            />
          </>
        )}
      </Card>

      <VideostoreDrafts
        open={draftsModalOpen}
        onClose={() => setDraftsModalOpen(false)}
        onEdit={(post) =>
          setEditVideostorePostDialog({ open: true, entity: post, isNew: false })
        }
        onDelete={(post) => setDeleteVideostorePostDialog({ open: true, entity: post })}
      />
      <EditVideostorePost
        videostorePost={editVideostorePostDialog.entity}
        open={editVideostorePostDialog.open}
        onClose={() =>
          setEditVideostorePostDialog((prev) => ({ open: false, entity: null, isNew: prev.isNew }))
        }
        isNew={editVideostorePostDialog.isNew}
      />
      <DeleteVideostorePost
        videostorePost={deleteVideostorePostDialog.entity}
        open={deleteVideostorePostDialog.open}
        onClose={() => setDeleteVideostorePostDialog({ open: false, entity: null })}
      />
      <ViewVideoDialog
        videostorePost={viewVideoDialog.entity}
        open={viewVideoDialog.open}
        onClose={() => setViewVideoDialog({ open: false, entity: null })}
      />
      <MultiUpload
        open={multiUploadDialog.open}
        onClose={() => setMultiUploadDialog({ open: false })}
      />
    </>
  );
};

export default Videostore;
